import { roadDistance, type TreeInstance, type ZoneDef, type ZoneLayout } from '@jianghu/game-data';
import { createRng } from '@jianghu/shared';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { part } from '../entities/geometry';

/** Shared time uniform driving wind sway in vegetation shaders. */
export const windUniforms = { uTime: { value: 0 } };

/** MeshStandardMaterial with vertex-shader wind sway proportional to height (instanced meshes). */
export function windMaterial(
  params: THREE.MeshStandardMaterialParameters,
  strength: number,
  heightScale: number,
): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial(params);
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = windUniforms.uTime;
    shader.vertexShader =
      'uniform float uTime;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        #ifdef USE_INSTANCING
          vec3 iPos = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
        #else
          vec3 iPos = vec3(0.0);
        #endif
        float hh = max(0.0, position.y) * ${heightScale.toFixed(3)};
        float ww = sin(uTime * 1.6 + iPos.x * 0.31 + iPos.z * 0.23) * 0.65 + sin(uTime * 2.9 + iPos.x * 0.77 - iPos.z * 0.4) * 0.3;
        transformed.x += ww * hh * hh * ${strength.toFixed(3)};
        transformed.z += ww * 0.55 * hh * hh * ${strength.toFixed(3)};`,
      );
  };
  m.customProgramCacheKey = () => `wind-${strength}-${heightScale}`;
  return m;
}

function geoPine(): { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry } {
  return {
    trunk: mergeGeometries([
      part.cyl(0.13, 0.22, 2.4, '#5a3c28', { pos: [0, 1.2, 0] }, 7),
    ]) as THREE.BufferGeometry,
    crown: mergeGeometries([
      part.cone(1.7, 2.3, '#2f5a32', { pos: [0, 2.5, 0] }, 9),
      part.cone(1.35, 2.0, '#376a39', { pos: [0, 3.6, 0] }, 9),
      part.cone(0.95, 1.7, '#3f7640', { pos: [0, 4.6, 0] }, 8),
      part.cone(0.5, 1.2, '#4a844a', { pos: [0, 5.5, 0] }, 7),
    ]) as THREE.BufferGeometry,
  };
}

function geoWillow(): { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry } {
  const rng = createRng(11);
  const crown: THREE.BufferGeometry[] = [
    part.ico(1.45, '#8fb35a', { pos: [0, 3.9, 0], scale: [1.2, 0.85, 1.2] }, 1),
    part.ico(1.0, '#9fc165', { pos: [0.9, 3.5, 0.5] }, 1),
    part.ico(1.05, '#87ab55', { pos: [-0.9, 3.6, -0.4] }, 1),
  ];
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const r = 1.3 + rng.next() * 0.5;
    const len = 1.6 + rng.next() * 1.0;
    crown.push(
      part.cone(
        0.16,
        len,
        i % 2 ? '#a7c46a' : '#93b85c',
        { pos: [Math.sin(a) * r, 3.5 - len / 2, Math.cos(a) * r], rot: [Math.PI, 0, 0] },
        4,
      ),
    );
  }
  return {
    trunk: mergeGeometries([
      part.cyl(0.2, 0.32, 3.0, '#5b4433', { pos: [0, 1.5, 0], rot: [0, 0, 0.08] }, 7),
      part.cyl(0.08, 0.14, 1.6, '#5b4433', { pos: [0.55, 3.0, 0], rot: [0, 0, -0.7] }, 5),
    ]) as THREE.BufferGeometry,
    crown: mergeGeometries(crown) as THREE.BufferGeometry,
  };
}

function geoMaple(): { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry } {
  return {
    trunk: mergeGeometries([
      part.cyl(0.15, 0.25, 2.3, '#4e3626', { pos: [0, 1.15, 0] }, 7),
      part.cyl(0.07, 0.12, 1.3, '#4e3626', { pos: [0.4, 2.4, 0.1], rot: [0.2, 0, -0.6] }, 5),
    ]) as THREE.BufferGeometry,
    crown: mergeGeometries([
      part.ico(1.35, '#f2f2f2', { pos: [0, 3.4, 0] }, 1),
      part.ico(1.0, '#e2e2e2', { pos: [1.0, 3.0, 0.3] }, 1),
      part.ico(0.95, '#d8d8d8', { pos: [-0.9, 3.1, -0.4] }, 1),
      part.ico(0.9, '#eaeaea', { pos: [0.2, 3.0, -1.0] }, 1),
      part.ico(0.85, '#dcdcdc', { pos: [-0.2, 4.2, 0.4] }, 1),
    ]) as THREE.BufferGeometry,
  };
}

function geoBamboo(): { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry } {
  const rng = createRng(5);
  const stalks: THREE.BufferGeometry[] = [];
  const leaves: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 9; i++) {
    const a = rng.range(0, Math.PI * 2);
    const r = rng.range(0.1, 0.9);
    const x = Math.sin(a) * r;
    const z = Math.cos(a) * r;
    const h = rng.range(5, 7.5);
    const tilt: [number, number, number] = [rng.range(-0.06, 0.06), 0, rng.range(-0.06, 0.06)];
    stalks.push(
      part.cyl(0.055, 0.07, h, i % 2 ? '#7a9a3a' : '#8aa84a', { pos: [x, h / 2, z], rot: tilt }, 5),
    );
    for (let k = 0; k < 2; k++) {
      leaves.push(
        part.ico(
          rng.range(0.45, 0.7),
          rng.pick(['#4f7a34', '#5d8a3a', '#46702e']),
          {
            pos: [x + rng.range(-0.5, 0.5), h - 0.4 - k * 0.9, z + rng.range(-0.5, 0.5)],
            scale: [1.3, 0.35, 1.0],
          },
          0,
        ),
      );
    }
  }
  return {
    trunk: mergeGeometries(stalks) as THREE.BufferGeometry,
    crown: mergeGeometries(leaves) as THREE.BufferGeometry,
  };
}

const MAPLE_TINTS = ['#c8432c', '#d9622b', '#e08a2a', '#c23a3a', '#d6a23a', '#b9532e'];

/** Instanced trees (two draw calls per species) with wind-swayed crowns. */
export function buildTrees(trees: TreeInstance[]): THREE.Group {
  const group = new THREE.Group();
  group.name = 'trees';
  const kinds: Record<string, () => { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry }> = {
    pine: geoPine,
    willow: geoWillow,
    maple: geoMaple,
    bamboo: geoBamboo,
  };
  const trunkMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95 });
  const crownMat = windMaterial({ vertexColors: true, roughness: 0.85, flatShading: true }, 0.05, 0.22);
  const bambooMat = windMaterial({ vertexColors: true, roughness: 0.8, flatShading: true }, 0.03, 0.18);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  const p = new THREE.Vector3();
  const col = new THREE.Color();
  for (const [kind, make] of Object.entries(kinds)) {
    const list = trees.filter((t) => t.kind === kind);
    if (!list.length) continue;
    const g = make();
    const trunk = new THREE.InstancedMesh(g.trunk, kind === 'bamboo' ? bambooMat : trunkMat, list.length);
    const crown = new THREE.InstancedMesh(g.crown, kind === 'bamboo' ? bambooMat : crownMat, list.length);
    list.forEach((t, i) => {
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), t.rot);
      s.setScalar(t.scale);
      p.set(t.x, 0, t.z);
      m.compose(p, q, s);
      trunk.setMatrixAt(i, m);
      crown.setMatrixAt(i, m);
      if (kind === 'maple') col.set(MAPLE_TINTS[i % MAPLE_TINTS.length] as string);
      else col.setHSL(0, 0, 0.88 + ((i * 37) % 23) / 100);
      crown.setColorAt(i, col);
    });
    for (const im of [trunk, crown]) {
      im.castShadow = true;
      im.receiveShadow = true;
      im.computeBoundingSphere();
      group.add(im);
    }
  }
  return group;
}

/** Instanced grass tufts, flowers and rice sprouts (client-only decoration, seeded). */
export function buildGrass(zone: ZoneDef, layout: ZoneLayout, density: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'grass';
  const rng = createRng(zone.seed + 404);
  const blades: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + rng.range(-0.3, 0.3);
    const h = rng.range(0.32, 0.55);
    const shape = new THREE.BufferGeometry();
    const bx = Math.sin(a) * 0.05;
    const bz = Math.cos(a) * 0.05;
    const tx = Math.sin(a) * 0.18;
    const tz = Math.cos(a) * 0.18;
    const px = Math.cos(a) * 0.045;
    const pz = -Math.sin(a) * 0.045;
    shape.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([bx - px, 0, bz - pz, bx + px, 0, bz + pz, tx, h, tz], 3),
    );
    const base = new THREE.Color('#3d5a26');
    const tip = new THREE.Color(rng.pick(['#a9c063', '#b8c86a', '#93b257']));
    shape.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(
        [base.r, base.g, base.b, base.r, base.g, base.b, tip.r, tip.g, tip.b],
        3,
      ),
    );
    shape.computeVertexNormals();
    // Normals point up for soft, even lighting of thin blades; both windings are emitted so the
    // blade is visible from either side without DoubleSide normal flipping (which darkens back faces).
    const n = shape.getAttribute('normal');
    for (let k = 0; k < n.count; k++) n.setXYZ(k, 0, 1, 0);
    blades.push(shape);
    const back = shape.clone();
    const bp = back.getAttribute('position');
    const bc = back.getAttribute('color');
    const swap = (attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): void => {
      const x = attr.getX(1),
        y = attr.getY(1),
        z = attr.getZ(1);
      attr.setXYZ(1, attr.getX(2), attr.getY(2), attr.getZ(2));
      attr.setXYZ(2, x, y, z);
    };
    swap(bp);
    swap(bc);
    blades.push(back);
  }
  const tuft = mergeGeometries(blades) as THREE.BufferGeometry;
  const grassMat = windMaterial({ vertexColors: true, roughness: 1 }, 0.35, 2.2);
  const flower = mergeGeometries([
    part.cyl(0.01, 0.012, 0.35, '#4a6a2a', { pos: [0, 0.17, 0] }, 3),
    part.ico(0.06, '#ffffff', { pos: [0, 0.37, 0] }, 0),
  ]) as THREE.BufferGeometry;
  const flowerMat = windMaterial({ vertexColors: true, roughness: 0.8 }, 0.4, 2.2);

  const accept = (x: number, z: number): boolean => {
    if (layout.world.overlaps(x, z, 0.35)) return false;
    if ((x - zone.plaza.x) ** 2 + (z - zone.plaza.z) ** 2 < (zone.plaza.r + 0.6) ** 2) return false;
    if (zone.fields.some((f) => x > f.minX - 0.5 && x < f.maxX + 0.5 && z > f.minZ - 0.5 && z < f.maxZ + 0.5))
      return false;
    for (const road of zone.roads) if (roadDistance(road.points, x, z) < road.width / 2 + 0.25) return false;
    return true;
  };
  const b = zone.bounds;
  const tuftCount = Math.round(26000 * density);
  const tufts = new THREE.InstancedMesh(tuft, grassMat, tuftCount);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  let n = 0;
  for (let i = 0; i < tuftCount * 3 && n < tuftCount; i++) {
    // Denser near the centre of the playable area.
    const x = rng.range(b.minX + 2, b.maxX - 2);
    const z = rng.range(b.minZ + 2, b.maxZ - 2);
    if (!accept(x, z)) continue;
    q.setFromAxisAngle(up, rng.range(0, Math.PI * 2));
    const sc = rng.range(0.7, 1.35);
    m.compose(new THREE.Vector3(x, 0, z), q, new THREE.Vector3(sc, sc * rng.range(0.8, 1.3), sc));
    tufts.setMatrixAt(n++, m);
  }
  tufts.count = n;
  tufts.receiveShadow = true;
  tufts.computeBoundingSphere();
  group.add(tufts);

  const flowerCount = Math.round(1600 * density);
  const flowers = new THREE.InstancedMesh(flower, flowerMat, flowerCount);
  const col = new THREE.Color();
  n = 0;
  for (let i = 0; i < flowerCount * 4 && n < flowerCount; i++) {
    const x = rng.range(b.minX + 2, b.maxX - 2);
    const z = rng.range(b.minZ + 2, b.maxZ - 2);
    if (!accept(x, z)) continue;
    const sc = rng.range(0.7, 1.2);
    m.compose(
      new THREE.Vector3(x, 0, z),
      q.setFromAxisAngle(up, rng.range(0, 6)),
      new THREE.Vector3(sc, sc, sc),
    );
    flowers.setMatrixAt(n, m);
    flowers.setColorAt(n, col.set(rng.pick(['#f2d65a', '#f4f0e6', '#e8829a', '#b49be8', '#f2a04a'])));
    n++;
  }
  flowers.count = n;
  flowers.computeBoundingSphere();
  group.add(flowers);

  // Rice sprouts in rows.
  const sprouts: THREE.Matrix4[] = [];
  for (const f of zone.fields) {
    for (let z = f.minZ + 0.6; z < f.maxZ; z += 1.2) {
      for (let x = f.minX + 0.5; x < f.maxX; x += 0.9 / Math.max(0.4, density)) {
        if (rng.next() < 0.15) continue;
        sprouts.push(
          new THREE.Matrix4().compose(
            new THREE.Vector3(x + rng.range(-0.1, 0.1), -0.02, z),
            q.setFromAxisAngle(up, rng.range(0, 6)),
            new THREE.Vector3(0.7, 0.9, 0.7),
          ),
        );
      }
    }
  }
  const rice = new THREE.InstancedMesh(
    tuft,
    windMaterial({ vertexColors: true, roughness: 1, color: '#c8e08a' }, 0.3, 2.2),
    sprouts.length,
  );
  sprouts.forEach((mm, i) => rice.setMatrixAt(i, mm));
  rice.computeBoundingSphere();
  group.add(rice);
  return group;
}
